import apiClient from './client'
import type { CreateTagRequest, Tag, UpdateTagRequest } from '@/types/models'
import { adaptTagResponse } from '@/lib/utils/api-adapter'
import { ApiError } from '@/types/api'

/** A list that stands in the way of deleting or merging a tag. */
export interface ConflictingList {
  id: string
  name: string
}

/**
 * The lists named by a 409 from DELETE /tags/{id} or POST /tags/{id}/merge.
 *
 * The API refuses to orphan a list's criteria and answers
 * `{"error": ..., "lists": [{id, name}]}` — the whole body reaches the client
 * on `ApiError.details`, so the UI can name the lists instead of showing a
 * dead end.
 */
export function conflictingLists(error: unknown): ConflictingList[] {
  if (!(error instanceof ApiError) || error.status !== 409) return []
  const lists = (error.details as { lists?: unknown } | undefined)?.lists
  if (!Array.isArray(lists)) return []
  return lists
    .filter((entry): entry is ConflictingList => !!entry && typeof entry.id === 'string')
    .map(entry => ({ id: entry.id, name: entry.name ?? entry.id }))
}

/**
 * Tag API endpoints (rivulet >= v0.14.0).
 */
export const tagsApi = {
  /**
   * List the user's tags with the number of live feeds carrying each.
   */
  async list(): Promise<Tag[]> {
    const response = await apiClient.get<any>('/api/v1/tags')
    return (response.tags ?? []).map(adaptTagResponse)
  },

  /**
   * Create a tag. The slug is derived from the name; an existing slug is
   * returned as-is rather than duplicated.
   */
  async create(data: CreateTagRequest): Promise<Tag> {
    const body: Record<string, unknown> = { name: data.name }
    if (data.color !== undefined) body.color = data.color
    const response = await apiClient.post<any>('/api/v1/tags', body)
    return adaptTagResponse(response)
  },

  /**
   * Rename and/or recolor. A rename re-derives the slug and rewrites it in
   * every list's criteria in the same transaction; 409 when another tag
   * already owns the new slug (merge instead). An empty color clears it.
   */
  async update(id: string, data: UpdateTagRequest): Promise<Tag> {
    const body: Record<string, unknown> = {}
    if (data.name !== undefined) body.name = data.name
    if (data.color !== undefined) body.color = data.color
    const response = await apiClient.patch<any>(`/api/v1/tags/${id}`, body)
    return adaptTagResponse(response)
  },

  /**
   * Delete a tag. Refused with 409 (and the referencing lists) while any
   * list's criteria mention it. Never cascades.
   */
  async remove(id: string): Promise<void> {
    await apiClient.delete<void>(`/api/v1/tags/${id}`)
  },

  /**
   * Fold `id` into `into`: feeds move, list criteria are rewritten, the
   * source is deleted — one transaction. Returns the target tag. 409 when
   * the rewrite would contradict a list.
   */
  async merge(id: string, into: string): Promise<Tag> {
    const response = await apiClient.post<any>(`/api/v1/tags/${id}/merge`, { into })
    return adaptTagResponse(response)
  },
}

export default tagsApi
